import { ID, Repository, PaginatedResult, Result } from '../../domain/types/common';
import { DomainError } from '../../domain/errors/domainError';
import { ErrorCode } from '../../domain/errors/errorCodes';
import { AsyncLock } from '../../utils/asyncLock';
import { getKvStore } from '../platform';

type BatchOperationResult<U> = {
  success: U[];
  failed: Array<{ item: U; error: Error }>;
};

// Module-level lock shared across all repository instances. Keyed by storage
// key, it serializes each key's get -> mutate -> set sequence so two concurrent
// operations in the same context can't read the same array and have the second
// write clobber the first (lost updates / resurrected deletions).
const storageLock = new AsyncLock();

export abstract class ChromeStorageRepository<
  TDomain extends { id: ID },
  TStorage extends { id: ID },
> implements Repository<TDomain>
{
  protected abstract toDomain(storage: TStorage): TDomain;
  protected abstract toStorage(domain: TDomain): TStorage;
  protected abstract toDomainList(storageList: TStorage[]): TDomain[];
  protected abstract toStorageList(domainList: TDomain[]): TStorage[];
  constructor(
    protected readonly storageKey: string,
    protected readonly entityName: string
  ) {}

  private maxRetries = 3;
  private readonly retryDelayMs = 100;

  protected async getStorageData(): Promise<TStorage[]> {
    return this.withRetry(async () => {
      try {
        const result = await getKvStore().get(this.storageKey);
        const storageData = result[this.storageKey];

        // Handle both formats: direct array or object with data property
        if (!storageData) {
          return [];
        }

        // If it's an object with a data property and that data is an array, use it
        if (
          typeof storageData === 'object' &&
          !Array.isArray(storageData) &&
          storageData.data &&
          Array.isArray(storageData.data)
        ) {
          return storageData.data;
        }

        // Otherwise, use the original data if it's an array
        if (Array.isArray(storageData)) {
          return storageData;
        }

        // If we get here, the data is in an unexpected format
        console.error(`Invalid storage format for ${this.entityName}`);
        return [];
      } catch (error) {
        const errorObj = error instanceof Error ? error : new Error(String(error));
        throw new DomainError(
          ErrorCode.STORAGE_ERROR,
          `Failed to read ${this.entityName} from storage`,
          errorObj
        );
      }
    });
  }

  protected async setStorageData(data: TStorage[]): Promise<void> {
    return this.withRetry(async () => {
      try {
        await getKvStore().set({ [this.storageKey]: data });
      } catch (error) {
        const errorObj = error instanceof Error ? error : new Error(String(error));
        throw new DomainError(
          ErrorCode.STORAGE_ERROR,
          `Failed to write ${this.entityName} to storage`,
          errorObj
        );
      }
    });
  }

  /**
   * Serializes a read-modify-write block against this repository's storage key.
   * Subclasses with their own get->mutate->set methods should wrap them in this
   * so they don't race with create/update/delete on the same key.
   */
  protected async withStorageLock<T>(operation: () => Promise<T>): Promise<T> {
    return storageLock.acquire(this.storageKey, operation);
  }

  private async withRetry<T>(operation: () => Promise<T>, retries = this.maxRetries): Promise<T> {
    try {
      return await operation();
    } catch (error) {
      if (retries > 0) {
        // Exponential backoff
        const delay = Math.pow(2, this.maxRetries - retries) * this.retryDelayMs;
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.withRetry(operation, retries - 1);
      }
      throw error;
    }
  }

  async findById(id: ID): Promise<TDomain | null> {
    const items = await this.getStorageData();

    if (!Array.isArray(items)) {
      console.error(`Storage for ${this.entityName} is not an array`);
      // Return null instead of throwing an error to avoid breaking the app
      return null;
    }

    const item = items.find(item => item.id === id);
    return item ? this.toDomain(item) : null;
  }

  async findAll(): Promise<TDomain[]> {
    const items = await this.getStorageData();
    return this.toDomainList(items);
  }

  async create(domain: TDomain): Promise<TDomain> {
    return this.withStorageLock(async () => {
      const storage = this.toStorage(domain);
      const items = await this.getStorageData();

      // Check for duplicate ID
      if (items.some(item => item.id === storage.id)) {
        throw new DomainError(
          ErrorCode.VALIDATION_ERROR,
          `${this.entityName} with ID ${storage.id} already exists`
        );
      }

      items.push(storage);
      await this.setStorageData(items);
      return domain;
    });
  }

  async update(id: ID, domain: Partial<TDomain>): Promise<TDomain> {
    return this.withStorageLock(async () => {
      const items = await this.getStorageData();
      const index = items.findIndex(item => item.id === id);

      if (index === -1) {
        throw new DomainError(ErrorCode.NOT_FOUND, `${this.entityName} with ID ${id} not found`);
      }

      // Full domain instances round-trip through the mapper. Plain partials
      // must NOT be mapped: the mapper produces undefined for every unmapped
      // field (and may call instance methods a partial doesn't have), and
      // spreading those undefineds over the stored record blanked real values
      // — e.g. update(id, { updatedAt }) wiped title/text/tags.
      let patch: Partial<TStorage>;
      if (typeof (domain as { toObject?: unknown }).toObject === 'function') {
        patch = this.toStorage(domain as TDomain);
      } else {
        patch = {};
        for (const [key, value] of Object.entries(domain)) {
          if (value !== undefined) {
            (patch as Record<string, unknown>)[key] = value;
          }
        }
      }

      const updatedStorage = { ...items[index], ...patch, id: items[index].id };
      items[index] = updatedStorage;
      await this.setStorageData(items);
      return this.toDomain(updatedStorage);
    });
  }

  async delete(id: ID): Promise<void> {
    return this.withStorageLock(async () => {
      const items = await this.getStorageData();

      const itemToDelete = items.find(item => item.id === id);
      if (!itemToDelete) {
        throw new DomainError(ErrorCode.NOT_FOUND, `${this.entityName} with ID ${id} not found`);
      }

      const filteredItems = items.filter(item => item.id !== id);
      await this.setStorageData(filteredItems);

      // Verify the item was actually removed
      const verificationItems = await this.getStorageData();
      const stillExists = verificationItems.some(item => item.id === id);
      if (stillExists) {
        console.error(`Failed to delete ${this.entityName} ${id}: item still present after write`);
      }
    });
  }

  protected paginateResults<T extends TStorage>(
    items: T[],
    page: number = 1,
    pageSize?: number,
    sortBy?: keyof T,
    sortDirection: 'asc' | 'desc' = 'asc'
  ): PaginatedResult<TDomain> {
    // Sort items if sortBy is provided
    let sortedItems = [...items];
    if (sortBy) {
      sortedItems.sort((a, b) => {
        const aValue = a[sortBy];
        const bValue = b[sortBy];
        if (aValue === bValue) return 0;
        const comparison = aValue < bValue ? -1 : 1;
        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }
    const totalCount = sortedItems.length;
    // An undefined/invalid pageSize means "no pagination" — return everything on
    // a single page. The old default of 10 silently truncated every unpaginated
    // caller (folder/prompt lists, getMaxOrderForParent, cascade delete,
    // mergeFolders) to the first 10 items, causing order collisions and partial
    // operations. Callers that genuinely want a page still pass an explicit size.
    const effectivePageSize = pageSize && pageSize > 0 ? pageSize : totalCount || 1;
    const totalPages = Math.max(1, Math.ceil(totalCount / effectivePageSize));
    const currentPage = Math.min(Math.max(1, page), totalPages);
    const startIndex = (currentPage - 1) * effectivePageSize;
    const endIndex = Math.min(startIndex + effectivePageSize, totalCount);
    const pageItems = sortedItems.slice(startIndex, endIndex);

    return {
      success: true,
      data: this.toDomainList(pageItems),
      pageInfo: {
        currentPage,
        pageSize: effectivePageSize,
        totalCount,
        hasNextPage: currentPage < totalPages,
        hasPreviousPage: currentPage > 1,
      },
    };
  }

  protected async withErrorHandling<U>(
    operation: () => Promise<U>,
    errorMessage: string
  ): Promise<Result<U>> {
    try {
      const result = await operation();
      return { success: true, data: result };
    } catch (error) {
      if (error instanceof DomainError) {
        return { success: false, error };
      }
      const errorObj = error instanceof Error ? error : new Error(String(error));
      return {
        success: false,
        error: new DomainError(ErrorCode.STORAGE_ERROR, errorMessage, errorObj),
      };
    }
  }

  protected sortItems<T extends TStorage>(
    items: T[],
    sortBy: keyof T,
    sortDirection: 'asc' | 'desc' = 'asc'
  ): T[] {
    return [...items].sort((a, b) => {
      const aValue = a[sortBy];
      const bValue = b[sortBy];

      if (aValue === bValue) return 0;
      const comparison = aValue < bValue ? -1 : 1;
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }

  protected filterItems<T extends TStorage>(items: T[], predicate: (item: T) => boolean): T[] {
    return items.filter(predicate);
  }

  protected async executeBatchOperation<U>(
    items: U[],
    operation: (item: U) => Promise<void>
  ): Promise<BatchOperationResult<U>> {
    const results = await Promise.allSettled(
      items.map(async item => {
        try {
          await operation(item);
          return { success: true as const, item };
        } catch (error) {
          const errorObj =
            error instanceof Error ? error : new Error(String(error) || 'Unknown error');
          return { success: false as const, item, error: errorObj };
        }
      })
    );

    const batchResult: BatchOperationResult<U> = {
      success: [],
      failed: [],
    };

    results.forEach(result => {
      if (result.status === 'fulfilled') {
        if (result.value.success) {
          batchResult.success.push(result.value.item);
        } else {
          batchResult.failed.push({
            item: result.value.item,
            error: result.value.error,
          });
        }
      } else {
        // Handle rejected promises
        const error =
          result.reason instanceof Error
            ? result.reason
            : new Error(result.reason ? String(result.reason) : 'Unknown batch operation error');

        // Create a placeholder item for tracking purposes
        const placeholderItem = {} as U;
        batchResult.failed.push({
          item: placeholderItem,
          error,
        });
      }
    });

    return batchResult;
  }
}
